/**
 * AssumptionGrid — the editable data grid for one assumption file.
 *
 * Features (ported 1:1 from the standalone Hub, typed + restyled):
 *   - per-cell editing with focus-based undo history (50 levels)
 *   - add / delete rows (gated by category_fix + new-vs-existing rules)
 *   - paste-from-Excel (tab-delimited), with 4 modes depending on
 *     category_fix / new / existing
 *   - clear / reset
 *   - CSV export (opens directly in Excel), rendered through a Nordic
 *     self-downloading page, with a popup-blocked in-app fallback
 *   - save with pipe-separated backend validation-error parsing
 *   - column-type-aware cell rendering: category_fix (read-only), category
 *     (dropdown), sub_category (cascading dropdown), text inputs
 *
 * Nordic styling throughout; framer-motion removed (entrance via `view-in`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  ClipboardPaste,
  Download,
  Eraser,
  Plus,
  Save,
  Table as TableIcon,
  Trash2,
  Undo2,
  X,
} from "lucide-react";

import ConfirmModal, { type ConfirmModalVariant } from "./ConfirmModal";
import type { GridRow, TemplateColumn, TemplateConfig } from "./types";

export interface AssumptionGridProps {
  data: GridRow[];
  fileName: string;
  onSave: (rows: GridRow[], newFileName?: string | null) => Promise<void>;
  onClose: () => void;
  templateConfig?: TemplateConfig | null;
}

interface ModalState {
  open: boolean;
  title: string;
  message: string;
  variant: ConfirmModalVariant;
  onConfirm: (() => void) | null;
}

interface SaveErrors {
  title: string;
  details: string[];
}

interface HistoryEntry {
  data: GridRow[];
  originalIndices: Set<number>;
}

const CATEGORY_TYPES = new Set(["category", "sub_category", "category_fix"]);

export default function AssumptionGrid({
  data,
  fileName,
  onSave,
  onClose,
  templateConfig,
}: AssumptionGridProps) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [gridData, setGridData] = useState<GridRow[]>(data);
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrors, setSaveErrors] = useState<SaveErrors | null>(null);
  const [modal, setModal] = useState<ModalState>({
    open: false,
    title: "",
    message: "",
    variant: "alert",
    onConfirm: null,
  });
  const [isExporting, setIsExporting] = useState(false);

  const [originalRowIndices, setOriginalRowIndices] = useState<Set<number>>(
    new Set(),
  );
  const [editableName, setEditableName] = useState(fileName);
  const [headers, setHeaders] = useState<string[]>([]);

  const isNewFile = fileName === "New Assumptions";

  const columnMap = useMemo(() => {
    const map: Record<string, TemplateColumn> = {};
    if (templateConfig?.columns) {
      templateConfig.columns.forEach((col) => {
        map[col.name] = col;
      });
    }
    return map;
  }, [templateConfig]);

  const hasCategoryFix = useMemo(() => {
    if (!templateConfig?.columns) return false;
    return templateConfig.columns.some((c) => c.type === "category_fix");
  }, [templateConfig]);

  const canModifyRows = isNewFile ? !hasCategoryFix : true;

  useEffect(() => {
    if (data && data.length > 0) {
      setGridData(data);
      if (templateConfig?.columns) {
        setHeaders(templateConfig.columns.map((c) => c.name));
      } else {
        setHeaders(Object.keys(data[0]));
      }
      setHistory([]);
      if (fileName !== "New Assumptions") {
        setOriginalRowIndices(new Set(data.map((_, i) => i)));
      } else {
        setOriginalRowIndices(new Set());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, templateConfig]);

  const addToHistory = (
    oldData: GridRow[],
    oldOriginalIndices: Set<number>,
  ) => {
    setHistory((prev) => [
      ...prev.slice(-49),
      {
        data: JSON.parse(JSON.stringify(oldData)) as GridRow[],
        originalIndices: new Set(oldOriginalIndices),
      },
    ]);
  };

  const handleCellFocus = useCallback(() => {
    addToHistory(gridData, originalRowIndices);
  }, [gridData, originalRowIndices]);

  const handleCellChange = (rowIndex: number, key: string, value: string) => {
    if (!isNewFile && originalRowIndices.has(rowIndex)) {
      const colDef = columnMap[key];
      if (colDef && CATEGORY_TYPES.has(colDef.type)) return;
    }
    const newData = [...gridData];
    newData[rowIndex] = { ...newData[rowIndex], [key]: value };

    // Reset sub_category dependents when their parent category changes.
    if (templateConfig?.columns) {
      templateConfig.columns.forEach((col) => {
        if (col.type === "sub_category" && col.main_category === key) {
          newData[rowIndex][col.name] = "";
        }
      });
    }
    setGridData(newData);
  };

  const handleAddRow = () => {
    addToHistory(gridData, originalRowIndices);
    const newRow: GridRow = {};
    headers.forEach((header) => {
      newRow[header] = columnMap[header]?.default || "";
    });
    setGridData([...gridData, newRow]);
  };

  const handleDeleteRow = (index: number) => {
    if (isNewFile && hasCategoryFix) return;
    if (!isNewFile && hasCategoryFix && originalRowIndices.has(index)) return;

    addToHistory(gridData, originalRowIndices);
    const newData = [...gridData];
    newData.splice(index, 1);

    const newOriginal = new Set<number>();
    originalRowIndices.forEach((i) => {
      if (i < index) newOriginal.add(i);
      else if (i > index) newOriginal.add(i - 1);
    });

    setGridData(newData);
    setOriginalRowIndices(newOriginal);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const previousState = history[history.length - 1];
    setGridData(previousState.data);
    setOriginalRowIndices(previousState.originalIndices);
    setHistory(history.slice(0, -1));
  };

  // ---- paste handling ----
  const [isPasteListening, setIsPasteListening] = useState(false);
  const pasteAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const pasteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePasteContent = () => {
    setIsPasteListening(true);
    setTimeout(() => pasteAreaRef.current?.focus(), 50);
    pasteTimerRef.current = setTimeout(() => setIsPasteListening(false), 8000);
  };

  const cancelPasteListening = () => {
    setIsPasteListening(false);
    if (pasteTimerRef.current) clearTimeout(pasteTimerRef.current);
  };

  const showAlert = (title: string, message: string) =>
    setModal({
      open: true,
      title,
      message,
      variant: "alert",
      onConfirm: () => setModal((m) => ({ ...m, open: false })),
    });

  const processPastedText = (clipboardText: string) => {
    if (!clipboardText.trim()) {
      showAlert("Empty Content", "No data was pasted. Copy data from Excel first.");
      return;
    }
    const lines = clipboardText.trim().split("\n");
    if (lines.length === 0) return;

    addToHistory(gridData, originalRowIndices);

    const pasteableHeaders = headers.filter((h) => {
      const colDef = columnMap[h];
      return !colDef || !CATEGORY_TYPES.has(colDef.type);
    });

    if (pasteableHeaders.length === 0) {
      showAlert("Paste Blocked", "All columns are restricted. Nothing to paste.");
      return;
    }

    if (hasCategoryFix && isNewFile) {
      // New file w/ fixed rows: paste into existing rows only.
      const newData = [...gridData];
      const rowsToPaste = Math.min(lines.length, newData.length);
      for (let i = 0; i < rowsToPaste; i++) {
        const values = lines[i].split("\t");
        let valueIdx = 0;
        pasteableHeaders.forEach((header) => {
          if (valueIdx < values.length) {
            newData[i] = { ...newData[i], [header]: values[valueIdx].trim() };
          }
          valueIdx++;
        });
      }
      setGridData(newData);
    } else if (hasCategoryFix && !isNewFile) {
      // Existing file w/ category_fix: paste into existing + append extras.
      const newData = [...gridData];
      const rowsToPasteIntoExisting = Math.min(lines.length, newData.length);
      for (let i = 0; i < rowsToPasteIntoExisting; i++) {
        const values = lines[i].split("\t");
        let valueIdx = 0;
        pasteableHeaders.forEach((header) => {
          if (valueIdx < values.length) {
            newData[i] = { ...newData[i], [header]: values[valueIdx].trim() };
          }
          valueIdx++;
        });
      }
      for (let i = newData.length; i < lines.length; i++) {
        const values = lines[i].split("\t");
        const rowObj: GridRow = {};
        headers.forEach((h) => {
          rowObj[h] = columnMap[h]?.default || "";
        });
        let valueIdx = 0;
        pasteableHeaders.forEach((header) => {
          if (valueIdx < values.length) {
            rowObj[header] = values[valueIdx].trim();
          }
          valueIdx++;
        });
        newData.push(rowObj);
      }
      setGridData(newData);
    } else {
      // Normal mode: create new rows from pasted data.
      const newRows: GridRow[] = lines.map((line) => {
        const values = line.split("\t");
        const rowObj: GridRow = {};
        headers.forEach((h) => {
          rowObj[h] = columnMap[h]?.default || "";
        });
        let valueIdx = 0;
        pasteableHeaders.forEach((header) => {
          if (valueIdx < values.length) {
            rowObj[header] = values[valueIdx].trim();
          }
          valueIdx++;
        });
        return rowObj;
      });

      const nonEmptyRows = newRows.filter((row) =>
        pasteableHeaders.some((h) => row[h] !== ""),
      );

      if (nonEmptyRows.length === 0) {
        showAlert("No Data", "No valid data found in pasted content.");
        return;
      }
      setGridData([...gridData, ...nonEmptyRows]);
    }
  };

  const handleCleanRows = () => {
    if (hasCategoryFix && templateConfig?.columns) {
      const categoryFixCol = templateConfig.columns.find(
        (c) => c.type === "category_fix",
      );
      if (categoryFixCol) {
        addToHistory(gridData, originalRowIndices);
        const fixedOptions = (categoryFixCol.options as string[]) || [];
        const newRows = fixedOptions.map((optionValue) => {
          const row: GridRow = {};
          headers.forEach((colName) => {
            if (colName === categoryFixCol.name) {
              row[colName] = optionValue;
            } else {
              row[colName] = columnMap[colName]?.default || "";
            }
          });
          return row;
        });
        setGridData(newRows);
        setOriginalRowIndices(new Set());
        return;
      }
    }
    addToHistory(gridData, originalRowIndices);
    setGridData([]);
    setOriginalRowIndices(new Set());
  };

  const handleClose = () => {
    setModal({
      open: true,
      title: "Unsaved Changes",
      message:
        "All unsaved changes will be lost. Are you sure you want to close?",
      variant: "confirm",
      onConfirm: () => {
        setModal((m) => ({ ...m, open: false }));
        onClose();
      },
    });
  };

  const handleSave = async () => {
    setSaveErrors(null);
    if (isNewFile) {
      if (!editableName.trim() || editableName.trim() === "New Assumptions") {
        setSaveErrors({
          title: "Invalid file name",
          details: [
            "Please change the file name before saving. The default name 'New Assumptions' is not allowed.",
          ],
        });
        return;
      }
    }
    setIsSaving(true);
    try {
      const gridDataComputed = gridData.map((r) => {
        const out = { ...r };
        headers.forEach((h) => {
          if (columnMap[h]?.type === "Totalizer" && templateConfig?.template_name === "Marketing Expense Assumptions") { const t = sumRightOf(h, r); out[h] = t === null ? "" : t.toFixed(2); }
        });
        return out;
      });

      await onSave(gridDataComputed, isNewFile ? editableName.trim() : null);
    } catch (err) {
      const rawMsg = err instanceof Error ? err.message : String(err);
      const parts = rawMsg
        .split(" | ")
        .map((s) => s.trim())
        .filter(Boolean);
      if (parts.length > 1 || rawMsg.includes("Row ")) {
        setSaveErrors({ title: "Validation errors found", details: parts });
      } else {
        setSaveErrors({ title: "Failed to save", details: [rawMsg] });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const exportToCsv = () => {
    setIsExporting(true);
    try {
      // Build a CSV from the grid in header order. Opens directly in Excel.
      // (Replaces a heavy XLSX library — the data is plain tabular with no
      // formatting, so CSV is the right, dependency-free tool.)
      const escapeCell = (val: unknown): string => {
        if (val === null || val === undefined) return '""';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
      };
      const csvRows: string[] = [headers.map(escapeCell).join(",")];
      for (const row of gridData) {
        csvRows.push(headers.map((h) => escapeCell(row[h])).join(","));
      }
      // Prepend a UTF-8 BOM so Excel reads non-ASCII characters correctly.
      const csv = "\uFEFF" + csvRows.join("\r\n");

      const baseName = (editableName || "modified_assumptions").replace(
        /\.[^.]+$/,
        "",
      );
      const exportName = `${baseName}.csv`;

      const base64 = btoa(unescape(encodeURIComponent(csv)));
      const dataUrl = `data:text/csv;charset=utf-8;base64,${base64}`;

      const newWin = window.open("", "_blank");
      if (newWin) {
        // Nordic-styled self-downloading page (runs outside the app's CSS,
        // so colors are inlined to match the Bone/Ink/Slate palette).
        newWin.document.write(
          `<!DOCTYPE html><html><head><title>Download ${exportName}</title>` +
            `<style>@import url('https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700&family=Roboto+Mono:wght@400;500&display=swap');</style></head>` +
            `<body style="margin:0;font-family:'Roboto Condensed',system-ui,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;background:#ece7de;color:#1e232a;">` +
            `<div style="text-align:center;padding:2.5rem;background:#f4f0e8;border:1px solid rgba(30,35,42,0.12);border-radius:10px;box-shadow:0 4px 24px rgba(30,35,42,.08);max-width:400px;">` +
            `<div style="width:48px;height:48px;margin:0 auto 16px;background:rgba(79,93,103,0.12);border-radius:50%;display:flex;align-items:center;justify-content:center;">` +
            `<svg width="24" height="24" fill="none" stroke="#4f5d67" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>` +
            `</div>` +
            `<h2 style="margin:0 0 8px;font-size:19px;font-weight:700;">Your file is ready</h2>` +
            `<p style="margin:0 0 20px;font-size:13.5px;color:#5b6571;">If the download does not start automatically, click the button below.</p>` +
            `<a id="dl" href="${dataUrl}" download="${exportName}" style="display:inline-block;padding:10px 28px;background:#4f5d67;color:#ece7de;border-radius:7px;text-decoration:none;font-family:'Roboto Mono',monospace;font-weight:500;font-size:12px;text-transform:uppercase;letter-spacing:0.12em;">Download</a>` +
            `</div>` +
            `<script>document.getElementById('dl').click();<\/script>` +
            `</body></html>`,
        );
        newWin.document.close();
      } else {
        // Popup blocked — in-app fallback via the modal.
        setModal({
          open: true,
          title: "Download Ready",
          message: "Your file is ready. Click the button below to download it.",
          variant: "success",
          onConfirm: () => {
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = exportName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setModal((m) => ({ ...m, open: false }));
          },
        });
      }
    } catch (err) {
      console.error("Export failed:", err);
      showAlert("Export Failed", "Could not generate the file. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // ---- cell rendering ----
  const cellInputClass =
    "w-full bg-transparent px-2 py-1.5 rounded border t-base outline-none text-[13px] font-serif";

  // Sum of every column to the right of a computed-sum column, in header order.
  const sumRightOf = (colName: string, row: GridRow): number | null => {
    const idx = headers.indexOf(colName);
    if (idx === -1) return null;
    let any = false;
    const total = headers.slice(idx + 1).reduce((s, h) => {
      const n = parseFloat(row[h]);
      if (Number.isFinite(n)) { any = true; return s + n; }
      return s;
    }, 0);
    return any ? total : null;
  };

  const renderCell = (rowIndex: number, header: string, row: GridRow) => {
    const colDef = columnMap[header];
    const value = row[header] || "";

    if (colDef?.type === "Totalizer" && templateConfig?.template_name === "Marketing Expense Assumptions") {
      const total = sumRightOf(header, row);
      return (
        <span
          className="block w-full px-2 py-1.5 rounded select-none text-[13px] font-serif font-medium"
          style={{ color: "var(--ink)", background: "var(--hover)" }}
        >
          {total === null ? "" : total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      );
    }

    const isCategoryType = colDef && CATEGORY_TYPES.has(colDef.type);
    if (!isNewFile && originalRowIndices.has(rowIndex) && isCategoryType) {
      return (
        <span
          className="block w-full px-2 py-1.5 rounded cursor-not-allowed select-none text-[13px] font-serif font-medium"
          style={{ color: "var(--ink-2)", background: "var(--hover)" }}
        >
          {value}
        </span>
      );
    }

    if (!colDef) {
      return (
        <input
          type="text"
          value={value}
          onFocus={handleCellFocus}
          onChange={(e) => handleCellChange(rowIndex, header, e.target.value)}
          className={cellInputClass}
          style={{ color: "var(--ink)", borderColor: "transparent" }}
        />
      );
    }

    switch (colDef.type) {
      case "category_fix":
        return (
          <span
            className="block w-full px-2 py-1.5 rounded cursor-not-allowed select-none text-[13px] font-serif font-medium"
            style={{ color: "var(--ink-2)", background: "var(--hover)" }}
          >
            {value}
          </span>
        );

      case "category": {
        const options = (colDef.options as string[]) || [];
        return (
          <select
            value={value}
            onFocus={handleCellFocus}
            onChange={(e) => handleCellChange(rowIndex, header, e.target.value)}
            className="w-full px-2 py-1.5 rounded border hairline t-base outline-none cursor-pointer text-[13px] font-serif"
            style={{ background: "var(--bg)", color: "var(--ink)" }}
          >
            <option value="">-- Select --</option>
            {[...options]
              .sort((a, b) => a.localeCompare(b))
              .map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
          </select>
        );
      }

      case "sub_category": {
        const mainCatName = colDef.main_category || "";
        const mainCatValue = row[mainCatName] || "";
        const optionsMap = (colDef.options as Record<string, string[]>) || {};
        const availableOptions = mainCatValue
          ? optionsMap[mainCatValue] || []
          : [];
        return (
          <select
            value={value}
            onFocus={handleCellFocus}
            onChange={(e) => handleCellChange(rowIndex, header, e.target.value)}
            disabled={!mainCatValue}
            className="w-full px-2 py-1.5 rounded border hairline t-base outline-none text-[13px] font-serif"
            style={{
              background: mainCatValue ? "var(--bg)" : "var(--hover)",
              color: "var(--ink)",
              cursor: mainCatValue ? "pointer" : "not-allowed",
              opacity: mainCatValue ? 1 : 0.6,
            }}
          >
            <option value="">
              {mainCatValue ? "-- Select --" : `Select ${mainCatName} first`}
            </option>
            {[...availableOptions]
              .sort((a, b) => a.localeCompare(b))
              .map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
          </select>
        );
      }

      default:
        return (
          <input
            type="text"
            value={value}
            onFocus={handleCellFocus}
            onChange={(e) => handleCellChange(rowIndex, header, e.target.value)}
            placeholder={
              colDef.type === "year"
                ? "YYYY"
                : colDef.type === "month"
                  ? "1-12"
                  : ""
            }
            className={cellInputClass}
            style={{ color: "var(--ink)", borderColor: "transparent" }}
          />
        );
    }
  };

  if (!headers || headers.length === 0) return null;

  const toolbarBtn =
    "px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.1em] rounded border hairline flex items-center gap-2 t-base hover:bg-[var(--hover)] focus-ring disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div
      className="rounded-lg border hairline view-in"
      style={{ background: "var(--bg-elev)" }}
    >
      {/* Header */}
      <div
        className="p-4 border-b hairline flex justify-between items-start"
        style={{ borderColor: "var(--line)" }}
      >
        <div>
          <h2
            className="font-serif text-[18px] leading-tight flex items-center gap-2"
            style={{ color: "var(--ink)" }}
          >
            <TableIcon size={18} strokeWidth={1.5} style={{ color: "var(--accent)" }} />
            Editing:{" "}
            {isNewFile ? (
              <input
                type="text"
                value={editableName}
                onChange={(e) => setEditableName(e.target.value)}
                className="bg-transparent outline-none text-[18px] font-serif px-1 min-w-[120px]"
                style={{
                  color: "var(--ink)",
                  borderBottom: "2px solid var(--accent)",
                }}
                placeholder="New Assumptions"
              />
            ) : (
              fileName
            )}
          </h2>
          <div className="flex items-center gap-3 mt-1">
            <p className="text-[12px] font-serif" style={{ color: "var(--ink-3)" }}>
              {gridData.length} rows loaded
            </p>
            {templateConfig?.template_name && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-[0.1em]"
                style={{ background: "var(--accent-soft)", color: "var(--accent)" }}
              >
                {templateConfig.template_name}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={handleClose}
          className="flex items-center gap-1 px-3 py-1.5 text-[12px] font-mono uppercase tracking-[0.12em] rounded t-base hover:bg-[var(--hover)] focus-ring"
          style={{ color: "var(--ink-2)" }}
        >
          <X size={14} strokeWidth={1.5} />
          Close
        </button>
      </div>

      {/* Toolbar */}
      <div
        className="px-4 py-3 border-b hairline flex flex-col sm:flex-row justify-between gap-3"
        style={{ background: "var(--bg)", borderColor: "var(--line)" }}
      >
        <div className="flex gap-2">
          <button
            onClick={handleUndo}
            disabled={history.length === 0}
            className={toolbarBtn}
            style={{ color: "var(--ink-2)", background: "var(--bg-elev)" }}
          >
            <Undo2 size={14} strokeWidth={1.5} />
            Revert
          </button>
          <button
            onClick={isPasteListening ? cancelPasteListening : handlePasteContent}
            className={toolbarBtn}
            style={{
              color: isPasteListening ? "var(--accent)" : "var(--ink-2)",
              background: isPasteListening ? "var(--accent-soft)" : "var(--bg-elev)",
              borderColor: isPasteListening ? "var(--accent)" : undefined,
            }}
          >
            <ClipboardPaste
              size={14}
              strokeWidth={1.5}
              className={isPasteListening ? "animate-pulse" : ""}
            />
            {isPasteListening ? "Press Ctrl+V..." : "Paste Content"}
          </button>
          <button
            onClick={handleCleanRows}
            className={toolbarBtn}
            style={{ color: "var(--ink-2)", background: "var(--bg-elev)" }}
          >
            <Eraser size={14} strokeWidth={1.5} />
            Clear
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={exportToCsv}
            disabled={isExporting}
            className={toolbarBtn}
            style={{ color: "var(--ink-2)", background: "var(--bg-elev)" }}
          >
            <Download size={14} strokeWidth={1.5} />
            {isExporting ? "Exporting..." : "Export"}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.1em] rounded flex items-center gap-2 t-base focus-ring disabled:opacity-50"
            style={{ background: "var(--accent)", color: "var(--bg)" }}
          >
            <Save size={14} strokeWidth={1.5} />
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* Validation errors */}
      {saveErrors && (
        <div
          className="mx-4 mt-3 mb-1 rounded-lg border overflow-hidden"
          style={{
            background: "var(--neg-soft, rgba(156,90,82,0.08))",
            borderColor: "var(--neg)",
          }}
        >
          <div
            className="flex items-center justify-between px-4 py-2.5 border-b"
            style={{
              background: "var(--neg-soft, rgba(156,90,82,0.14))",
              borderColor: "var(--neg)",
            }}
          >
            <div className="flex items-center gap-2">
              <AlertCircle size={16} strokeWidth={1.5} style={{ color: "var(--neg)" }} />
              <span
                className="text-[13px] font-serif font-semibold"
                style={{ color: "var(--neg)" }}
              >
                {saveErrors.title}
              </span>
              <span className="text-[11px] font-mono" style={{ color: "var(--neg)" }}>
                ({saveErrors.details.length}{" "}
                {saveErrors.details.length === 1 ? "error" : "errors"})
              </span>
            </div>
            <button
              onClick={() => setSaveErrors(null)}
              className="p-1 rounded t-base focus-ring"
              style={{ color: "var(--neg)" }}
              aria-label="Dismiss errors"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          </div>
          <div className="px-4 py-3 max-h-48 overflow-y-auto space-y-1.5">
            {saveErrors.details.map((detail, idx) => {
              const rowMatch = detail.match(/^Row\s+(\d+):\s*(.+)$/);
              return (
                <div key={idx} className="flex items-start gap-2 text-[13px]">
                  {rowMatch ? (
                    <>
                      <span
                        className="flex-shrink-0 px-1.5 py-0.5 rounded text-[11px] font-mono font-bold"
                        style={{
                          background: "var(--neg)",
                          color: "var(--bg)",
                        }}
                      >
                        Row {rowMatch[1]}
                      </span>
                      <span className="font-serif" style={{ color: "var(--neg)" }}>
                        {rowMatch[2]}
                      </span>
                    </>
                  ) : (
                    <span className="font-serif" style={{ color: "var(--neg)" }}>
                      {detail}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grid */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead
            className="border-b hairline"
            style={{ background: "var(--bg-elev)", borderColor: "var(--line)" }}
          >
            <tr>
              <th
                className="px-6 py-3 w-12 text-center text-[10px] font-mono uppercase tracking-[0.14em]"
                style={{ color: "var(--ink-4)" }}
              >
                #
              </th>
              {headers.map((header) => (
                <th
                  key={header}
                  className="px-6 py-3 text-[10px] font-mono uppercase tracking-[0.14em] whitespace-nowrap"
                  style={{ color: "var(--ink-2)" }}
                >
                  {header}
                </th>
              ))}
              {canModifyRows && <th className="px-4 py-3 w-16" />}
            </tr>
          </thead>
          <tbody>
            {gridData.length === 0 ? (
              <tr>
                <td
                  colSpan={headers.length + 2}
                  className="px-6 py-8 text-center font-serif text-[14px]"
                  style={{ color: "var(--ink-4)" }}
                >
                  No rows available. Click &quot;Add Row&quot; to start.
                </td>
              </tr>
            ) : (
              gridData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="border-b hairline t-base group hover:bg-[var(--hover)]"
                  style={{ borderColor: "var(--line)" }}
                >
                  <td
                    className="px-6 py-4 text-center text-[11px] font-mono"
                    style={{ color: "var(--ink-4)" }}
                  >
                    {rowIndex + 1}
                  </td>
                  {headers.map((header) => (
                    <td key={`${rowIndex}-${header}`} className="px-2 py-2">
                      {renderCell(rowIndex, header, row)}
                    </td>
                  ))}
                  {canModifyRows && (
                    <td className="px-4 py-2 text-right opacity-0 group-hover:opacity-100 t-base">
                      <div className="flex justify-end gap-1">
                        {(!hasCategoryFix || !originalRowIndices.has(rowIndex)) && (
                          <button
                            onClick={() => handleDeleteRow(rowIndex)}
                            className="p-1 rounded t-base focus-ring"
                            style={{ color: "var(--ink-3)" }}
                            aria-label="Delete row"
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add row */}
      {canModifyRows && (
        <div
          className="p-2 border-t hairline"
          style={{ background: "var(--bg)", borderColor: "var(--line)" }}
        >
          <button
            onClick={handleAddRow}
            className="w-full py-2 flex items-center justify-center gap-2 text-[13px] font-serif rounded-lg t-base focus-ring"
            style={{
              color: "var(--ink-3)",
              border: "1px dashed var(--line)",
            }}
          >
            <Plus size={16} strokeWidth={1.5} />
            Add Row
          </button>
        </div>
      )}

      {/* Hidden paste capture */}
      {isPasteListening && (
        <textarea
          ref={pasteAreaRef}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData("text/plain");
            cancelPasteListening();
            processPastedText(text);
          }}
          onBlur={() => {
            if (isPasteListening) {
              setTimeout(() => pasteAreaRef.current?.focus(), 100);
            }
          }}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />
      )}

      <ConfirmModal
        isOpen={modal.open}
        title={modal.title}
        message={modal.message}
        variant={modal.variant}
        onConfirm={modal.onConfirm || (() => setModal((m) => ({ ...m, open: false })))}
        onCancel={() => setModal((m) => ({ ...m, open: false }))}
      />
    </div>
  );
}
